import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { IPlotRange } from '../models/range';

interface ITimeRangeHistory {
    plotId: string;
    timeRange: IPlotRange;
}

const MINIMUM_RANGE = 30 * 1000; // 30 seconds;
const ZOOM_IN_RATIO = 2 / 3;
const ZOOM_OUT_RATIO = 3 / 2;
const PAN_LEFT_RATIO = -2 / 3;
const PAN_RIGHT_RATIO = 2 / 3;

@Injectable({ providedIn: 'root' })
export class TimeRangeService {
    timeRangesMap: Map<string, BehaviorSubject<IPlotRange>> = new Map();
    history$: BehaviorSubject<ITimeRangeHistory[]> = new BehaviorSubject([]);
    undoStack: ITimeRangeHistory[] = [];
    redoStack: ITimeRangeHistory[] = [];
    synchronizeRanges = false;

    /** add a new plots time range to the map */
    initPlotTimeRange( plotId: string, timeRange: IPlotRange ) {
        this.timeRangesMap.set( plotId, new BehaviorSubject( timeRange ) );
    }

    /** return the time range for a specific plot */
    getPlotTimeRange( plotId: string ) {
        if ( this.timeRangesMap.has( plotId ) ) {
            return this.timeRangesMap.get( plotId ).getValue();
        }
    }

    getGlobalTimeRange() {
        return Array.from( this.timeRangesMap.values() )[ 0 ];
    }

    /** return the observable that updates whenever we update this plots time range */
    getPlotTimeRange$( plotId: string ) {
        if ( this.timeRangesMap.has( plotId ) ) {
            return this.timeRangesMap.get( plotId );
        }
    }

    /** get the time range change history for all plots */
    getTimeHistory$() {
        return this.history$;
    }

    /** return how many entries in the history stack belong to plotId */
    getPlotHistoryLength( plotId: string ) {
        if ( this.timeRangesMap.has( plotId ) ) {
            return this.history$.getValue().filter( history => history.plotId === plotId ).length;
        }
        return -1;
    }

    /**
     * add a new entry to plot time range history
     * target - 'all' means the change was applied to all plots
     */
    addToHistory( plotId: string, timeRange: IPlotRange ) {
        this.history$.next([
            ...this.history$.value,
            {
                plotId: plotId,
                timeRange: timeRange
            }
        ]);
    }

    /** set the time range for a plot */
    setPlotTimeRange( plotId: string, timeRange: IPlotRange, addToHistory = true ) {
        if ( this.timeRangesMap.has( plotId ) ) {
            const timeRangeSubject = this.timeRangesMap.get( plotId );

            if ( addToHistory ) {
                // add the old range to the history stack
                this.addToHistory( plotId, timeRangeSubject.getValue() );
            }

            timeRangeSubject.next( timeRange );
        }
    }

    /** set the time range for all plots */
    setAllPlotsTimeRange( timeRange: IPlotRange, addToHistory = true ) {
        Array.from( this.timeRangesMap.keys() ).forEach( plotId => {
            this.setPlotTimeRange( plotId, timeRange, addToHistory );
        });
    }

    /** undo the last time range change to a specific plot */
    undoPlotTimeRangeChange( plotId: string ) {
        // go backwards through the history stack until we find the plotId
        for ( let i = this.history$.value.length - 1; i >= 0; i-- ) {
            if (this.history$.value[i].plotId === plotId ) {
                const newTimeRange = this.history$.value.splice( i, 1 )[0];
                this.setPlotTimeRange( newTimeRange.plotId, newTimeRange.timeRange, false );
                break;
            }
        }
        this.history$.next( this.history$.getValue() );
    }

    /** this will undo the last change to 'all' plots */
    undoGlobalTimeRangeChange() {
        Array.from( this.timeRangesMap.keys() ).forEach( plotId => {
            this.undoPlotTimeRangeChange( plotId );
        });
    }

    // TODO: implement this
    /** redo the last time range change undone by the user */
    redoTimeRangeChange() {

    }

    // TODO: implement this
    /** redo the last action that was performed on this plotId */
    redoPlotTimeRangeChange( plotId: string ) {

    }

    /**
     * Sets a new range (both total and visible ranges) based on a duration,
     * with the center point of the currently visible range at the center of the new range.
     * @param duration The duration of the new range, in milliseconds.
     */
    setRangeByDuration( plotId: string, duration: number ) {
        const range = this.getPlotTimeRange( plotId );
        let end = range.end;
        let start = range.start;
        const isDate = range.isDate;

        const currentRange = end - start;
        const center = currentRange / 2 + start;

        // set the new range based on the old center point and the new duration
        start = center - duration / 2;
        end = center + duration / 2;

        if ( duration > MINIMUM_RANGE && start < end ) { // define a minimum range
            this.setPlotTimeRange( plotId, { start: start, end: end, isDate: isDate });
        }
    }

    setAllRangesByDuration( duration: number ) {
        Array.from( this.timeRangesMap.keys() ).forEach( plotId => {
            this.setRangeByDuration( plotId, duration );
        });
    }

    /**
     * Zooms in or out relative to the current visible range, setting a new range (both total and visible ranges).
     * If `ratio > 1`, the new range will be longer than the current range, effectively zooming out.
     * If `ratio < 1`, the new range will be shorter than the current range, effectively zooming in.
     *
     * @param ratio The ratio of the duration of the new range to the duration of the current visible range.
     */
    zoom( plotId: string, ratio: number ) {
        const range = this.getPlotTimeRange( plotId );
        this.setRangeByDuration( plotId, ( range.end - range.start ) * ratio );
    }

    /** Zooms the plot by an amount defined by the constant `ZOOM_OUT_RATIO` */
    zoomOut( plotId: string ) {
        this.zoom( plotId, ZOOM_OUT_RATIO );
    }

    /** zoom all plots out */
    zoomOutAll() {
        Array.from( this.timeRangesMap.keys() ).forEach( plotId => {
            this.zoomOut( plotId );
        });
    }

    /** Zooms the plot by an amount defined by the constant `ZOOM_IN_RATIO` */
    zoomIn( plotId: string ) {
        this.zoom( plotId, ZOOM_IN_RATIO );
    }

    /** zoom all plots in */
    zoomInAll() {
        Array.from( this.timeRangesMap.keys() ).forEach( plotId => {
            this.zoomIn( plotId );
        });
    }

    /**
     * Pans the plot by a defined ratio, relative to the current visible range. Sets a new range (both total and visible).
     * If `ratio < 0`, the plot will pan to the left.
     * If `ratio > 0`, the plot will pan to the right.
     * A value of 1 corresponds to the currently visible duration, so if `ratio = 1`, the plot will pan one "screen" to the right, i.e.
     * the new start date will be equal to the old end date.
     *
     * @param ratio The ratio representing how far to pan. The ratio represents `(newStart - oldStart) / (oldEnd - oldStart)`
     */
    pan( plotId: string, ratio: number ) {
        const range = this.getPlotTimeRange( plotId );
        const end = range.end;
        const start = range.start;
        const isDate = range.isDate;

        const currentDuration = end - start;
        const newStart = ratio * currentDuration + start;
        this.setPlotTimeRange( plotId, { start: newStart, end: newStart + currentDuration, isDate: isDate });
    }

    /** Pans the plot by an amount defined by the constant `PAN_LEFT_RATIO` */
    panLeft( plotId: string ) {
        this.pan( plotId, PAN_LEFT_RATIO );
    }

    /** pan all plots left ( backwards in time ) */
    panLeftAll() {
        Array.from( this.timeRangesMap.keys() ).forEach( plotId => {
            this.panLeft( plotId );
        });
    }

    /** Pans the plot by an amount defined by the constant `PAN_RIGHT_RATIO` */
    panRight( plotId: string ) {
        this.pan( plotId, PAN_RIGHT_RATIO );
    }

    /** pan all plots right ( forwards in time ) */
    panRightAll() {
        Array.from( this.timeRangesMap.keys() ).forEach( plotId => {
            this.panRight( plotId );
        });
    }
}
